import { useAuth } from '../auth/AuthProvider';
import { can } from '@gwc/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { archiveApi, type ArchiveSearchParams } from '../api/archive';
import { eventDate } from '../lib/format';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Pagination } from '../components/Pagination';
import { PageSkeleton } from '../components/Skeleton';

const PAGE_SIZE = 25;

export function Archive() {
  const archiveRoles = useAuth().user?.roles ?? [];
  const [params, setParams] = useState<ArchiveSearchParams>({});
  const [page, setPage] = useState(1);
  const debouncedQ = useDebouncedValue(params.q ?? '');

  const searchParams = {
    ...params,
    q: debouncedQ || undefined,
    page: String(page),
    pageSize: String(PAGE_SIZE),
  };

  const { data: facets } = useQuery({ queryKey: ['facets'], queryFn: () => archiveApi.facets() });
  const { data, isLoading, isError } = useQuery({
    queryKey: ['archive', searchParams],
    queryFn: () => archiveApi.search(searchParams),
  });

  const set = (patch: Partial<ArchiveSearchParams>) => {
    setParams((p) => ({ ...p, ...patch }));
    setPage(1);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {can(archiveRoles, 'history:contribute') && (
          <Link className="btn-ghost" to="/contribute">
            Contribute a memory / files
          </Link>
        )}
        {can(archiveRoles, 'records:approve') && (
          <Link className="btn-ghost" to="/contribute/review">
            Review contributions
          </Link>
        )}
        {can(archiveRoles, 'records:contribute') && (
          <Link className="btn-ghost" to="/archive-entry">
            Add / review paper records
          </Link>
        )}
        {can(archiveRoles, 'records:approve') && (
          <Link className="btn-ghost" to="/reports">
            Report builder
          </Link>
        )}
        {can(archiveRoles, 'import:run') && (
          <Link className="btn-ghost" to="/imports">
            Import CSV / Excel
          </Link>
        )}
      </div>
      <h1 className="mb-6 font-display text-3xl text-cream-50">Tasting Archive</h1>

      <label className="mb-3 block">
        Sort past tastings
        <select
          className="field"
          value={params.sort ?? 'newest'}
          onChange={(e) => set({ sort: e.target.value })}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="title">Title</option>
        </select>
      </label>
      <div className="card mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <input
          className="field lg:col-span-2"
          placeholder="Search title, theme, notes…"
          aria-label="Search archive"
          value={params.q ?? ''}
          onChange={(e) => set({ q: e.target.value })}
        />
        <select
          className="field"
          aria-label="Filter by year"
          value={params.year ?? ''}
          onChange={(e) => set({ year: e.target.value })}
        >
          <option value="">Any year</option>
          {facets?.years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select
          className="field"
          aria-label="Filter by producer"
          value={params.producer ?? ''}
          onChange={(e) => set({ producer: e.target.value })}
        >
          <option value="">Any producer</option>
          {facets?.producers.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          className="field"
          aria-label="Filter by varietal"
          value={params.varietal ?? ''}
          onChange={(e) => set({ varietal: e.target.value })}
        >
          <option value="">Any varietal</option>
          {facets?.varietals.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select
          className="field"
          aria-label="Filter by region"
          value={params.regionLocation ?? ''}
          onChange={(e) => set({ regionLocation: e.target.value })}
        >
          <option value="">Any region</option>
          {facets?.regionLocations.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            setParams({});
            setPage(1);
          }}
        >
          Clear
        </button>
      </div>

      {isLoading ? (
        <PageSkeleton lines={5} />
      ) : isError ? (
        <p className="text-wine-300">Could not search the archive. Try again.</p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-cream-300/50">
          No tastings found. Completed tastings and imported history will appear here.
        </p>
      ) : (
        <>
          <p className="mb-3 text-xs text-cream-300/50">{data.total} tasting(s)</p>
          <div className="space-y-3">
            {data.items.map((e) => (
              <Link
                key={e.id}
                to={`/archive/${e.id}`}
                className="card block transition hover:border-gold-600/40"
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg text-cream-50">{e.title}</span>
                  <span className="text-xs text-cream-300/50">{eventDate(e)}</span>
                </div>
                <div className="text-xs text-cream-300/60">
                  {e.theme ?? '—'} · {e.regionLocation ?? 'Location unknown'}
                </div>
              </Link>
            ))}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}
